import { FormField, isSome, Maybe } from '@crocodille/common';
import { RadioGroup } from '@headlessui/react';
import cn from 'classnames';
import React from 'react';

import Check from '../../elements/check/check';
import styles from './payment-select.module.scss';

export interface PaymentSelectProps {
  readonly value: Maybe<string>;
  readonly invalid?: boolean;
  onChange(value: Maybe<string>): void;
  onBlur?(): void;
  readonly className?: string;
  readonly disabled?: boolean;
}

/**
 * Special component for selecting payment methods.
 */
const PaymentSelectElement: React.FC<
  React.PropsWithChildren<PaymentSelectProps>
> = ({ value, invalid, onChange, onBlur, className, children, disabled }) => {
  return (
    <RadioGroup
      value={value}
      onChange={onChange}
      onBlur={onBlur}
      disabled={disabled}
      className={cn(
        styles.container,
        { [styles.isInvalid]: invalid, [styles.isDisabled]: disabled },
        className,
      )}
    >
      {children}
    </RadioGroup>
  );
};

export interface PaymentSelectOptionProps {
  readonly value: string;
  readonly label: string;
  readonly description?: Maybe<string>;
  readonly icon: React.ReactElement;
  readonly disabled?: boolean;
}

const PaymentSelectOption: React.FC<
  React.PropsWithChildren<PaymentSelectOptionProps>
> = ({ value, label, description, icon, children, disabled }) => {
  return (
    <RadioGroup.Option as={React.Fragment} value={value} disabled={disabled}>
      {({ checked, active, disabled }) => (
        <div
          title={label}
          className={cn(styles.option, {
            [styles.isChecked]: checked,
            [styles.isActive]: active,
            [styles.isDisabled]: disabled,
          })}
        >
          <div className={styles.labelContainer}>
            <div className={styles.icon}>{icon}</div>
            {description ? (
              <div className={styles.description}>{description}</div>
            ) : null}
            {children}
          </div>
          <Check value={checked} />
        </div>
      )}
    </RadioGroup.Option>
  );
};

const PaymentSelect = Object.assign(PaymentSelectElement, {
  Option: PaymentSelectOption,
});

export function fieldToPaymentSelectProps({
  value,
  onChange,
  onBlur,
  disabled,
  error,
}: FormField<Maybe<string>>): Pick<
  PaymentSelectProps,
  'value' | 'onChange' | 'onBlur' | 'invalid' | 'disabled'
> {
  return {
    value,
    onChange,
    onBlur,
    disabled,
    invalid: isSome(error),
  };
}

export default PaymentSelect;
